// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Domain glossary + inline click-to-show popover. Any element carrying
// data-term="key" becomes a dotted "explain" link; clicking it shows the
// definition in a fixed popover positioned near the click. Escape / click-away
// dismisses. Matches the Artemis Tracker pattern.

export const GLOSSARY: Record<string, { term: string; def: string }> = {
  tpp: {
    term: 'Two-party-preferred (TPP)',
    def: 'After preferences are distributed, the final split between Labor and the Liberal/National Coalition. The AEC calculates a notional TPP for every seat — even ones the two majors did not finish first and second in — so seats can be compared on one national line.',
  },
  tcp: {
    term: 'Two-candidate-preferred (TCP)',
    def: 'The final count in each seat between the actual last two candidates — whoever they are. In most seats that is Labor v Coalition, but in a "teal" or Greens seat it can be an independent v Liberal. The TCP decides who actually won and by what margin.',
  },
  swing: {
    term: 'Swing',
    def: 'The change in a party\'s two-party-preferred share since the last election. A "+2.6 swing to Labor" means Labor\'s TPP share rose 2.6 points. The AEC adjusts for boundary redistributions, so the swing compares like with like.',
  },
  margin: {
    term: 'Margin',
    def: 'How far above 50% the winner finished on the two-candidate-preferred count. A 0.02% margin (Bradfield) is 26 votes; a 25% margin (Maranoa) is a fortress. Marginal seats decide elections.',
  },
  preferences: {
    term: 'Preferences',
    def: 'Australia uses full preferential voting: you number every candidate. If no one has a majority of first preferences, the lowest candidate is excluded and their ballots flow to the next preference marked. This repeats until someone passes 50%.',
  },
  nonclassic: {
    term: 'Non-classic seat',
    def: 'A seat where the final two candidates were not one Labor and one Coalition — for example a teal independent against a Liberal, or the Greens against Labor. The notional Labor-v-Coalition TPP still exists but did not decide the seat.',
  },
  firstpref: {
    term: 'First preferences',
    def: 'The primary vote — the number 1 on the ballot, before any preferences are distributed. In 2025 the two major parties together took barely two-thirds of first preferences, a record low.',
  },
  informal: {
    term: 'Informal vote',
    def: 'A ballot that could not be counted — left blank, numbered incorrectly, or otherwise not a valid preference. Informal votes are excluded from the result; a high rate can signal confusion or protest.',
  },
  turnout: {
    term: 'Turnout',
    def: 'The share of enrolled voters who cast a ballot. Voting is compulsory in Australia, so turnout is high (about 90%); the gap from 100% is people who did not vote despite being enrolled.',
  },
  pendulum: {
    term: 'Mackerras pendulum',
    def: 'A chart that lines every seat up by its margin, most marginal in the middle, safest at the edges, Labor on one side and the Coalition on the other. It shows at a glance how many seats a given uniform swing would flip.',
  },
};

let popover: HTMLDivElement | null = null;

function ensurePopover(): HTMLDivElement {
  if (!popover) {
    popover = document.createElement('div');
    popover.className = 'glossary-popover';
    popover.setAttribute('role', 'dialog');
    document.body.appendChild(popover);
  }
  return popover;
}

function hide(): void {
  if (popover) popover.classList.remove('visible');
}

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest<HTMLElement>('[data-term]');
    if (link) {
      const key = link.getAttribute('data-term')!;
      const entry = GLOSSARY[key];
      if (!entry) return;
      e.preventDefault();
      e.stopPropagation();
      const el = ensurePopover();
      el.innerHTML = `<h4>${entry.term}</h4><p>${entry.def}</p>`;
      el.classList.add('visible');
      const rect = link.getBoundingClientRect();
      const pw = Math.min(320, window.innerWidth - 24);
      el.style.width = `${pw}px`;
      let left = rect.left;
      if (left + pw > window.innerWidth - 12) left = window.innerWidth - pw - 12;
      el.style.left = `${Math.max(12, left)}px`;
      const below = rect.bottom + 8;
      const ph = el.offsetHeight;
      el.style.top = below + ph > window.innerHeight - 12
        ? `${Math.max(12, rect.top - ph - 8)}px`
        : `${below}px`;
      return;
    }
    if (popover && !popover.contains(e.target as Node)) hide();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
  window.addEventListener('scroll', hide, true);
}

/** Wrap a jargon term in the UI with an inline "explain" affordance. */
export function term(key: string, label?: string): string {
  const entry = GLOSSARY[key];
  const text = label ?? (entry ? entry.term : key);
  return `<span class="glossary-link" data-term="${key}" tabindex="0" role="button" aria-label="Explain: ${text}">${text}</span>`;
}
